import type { TypeUser } from '../types/User'
import { type ModuleAccessCheck, type PermissionAccessCheck, resolveAccessibleLandingRoute } from './routeAccess'

export type ContextSelectionRoute = 'business-options-menu' | 'branch-selection' | 'location-selection'

/** Routes that must not be interrupted by context bootstrap redirects. */
export const CONTEXT_REDIRECT_EXEMPT_ROUTES: ReadonlySet<string> = new Set([
  'business-registration',
  'login',
  'signup',
  'recover-password',
  'reset-password',
  'reset-password-success',
  'recover-password-email',
  'invited-user-activation',
])

/** Pre-shell routes used while establishing business → branch → location context. */
export const CONTEXT_SELECTION_ROUTES: ReadonlySet<string> = new Set([
  'business-options-menu',
  'branch-selection',
  'location-selection',
])

export function hasActiveBranch(user?: TypeUser | null): boolean {
  return Boolean(user?.active_branch_uuid)
}

export function hasActiveLocation(user?: TypeUser | null): boolean {
  return Boolean(user?.active_location_id || user?.active_inventory_location)
}

/**
 * Resolve which pre-shell picker (if any) the user must complete before the app shell.
 * Trusts session-level requires_* flags and active branch/location fields only —
 * not business-list capability hints on active_business.
 */
export function resolveRequiredContextRoute(user?: TypeUser | null): ContextSelectionRoute | null {
  if (!user?.active_business) {
    return 'business-options-menu'
  }

  if (user.requires_branch_selection === true || !hasActiveBranch(user)) {
    return 'branch-selection'
  }

  if (user.requires_inventory_location_selection === true || !hasActiveLocation(user)) {
    return 'location-selection'
  }

  return null
}

/** Destination after switch-active-business using response flags, then session fields. */
export function resolveRouteAfterBusinessSwitch(
  flags: {
    requires_branch_selection?: boolean
    requires_inventory_location_selection?: boolean
  },
  user?: TypeUser | null,
): ContextSelectionRoute | 'dashboard' {
  if (flags.requires_branch_selection === true) {
    return 'branch-selection'
  }
  if (flags.requires_inventory_location_selection === true) {
    return 'location-selection'
  }
  if (!hasActiveBranch(user)) {
    return 'branch-selection'
  }
  if (!hasActiveLocation(user)) {
    return 'location-selection'
  }
  return 'dashboard'
}

/** Destination after POST active/branch using response flag + updated auth user. */
export function resolveRouteAfterBranchSelection(
  requiresInventoryLocationSelection: boolean | undefined,
  user?: TypeUser | null,
): ContextSelectionRoute | 'dashboard' {
  if (requiresInventoryLocationSelection === true || !hasActiveLocation(user)) {
    return 'location-selection'
  }
  return 'dashboard'
}

/**
 * Turn a context-picker result into the named route the app should open.
 * `'dashboard'` is replaced by the authorized role landing route — never a URL.
 * Landing uses the same sidebar module and permission gates as typed URLs.
 */
export function resolvePostAuthRoute(
  destination: ContextSelectionRoute | 'dashboard' | null,
  defaultDashboard: unknown,
  hasModule: ModuleAccessCheck,
  hasPermission: PermissionAccessCheck,
): string {
  if (destination && destination !== 'dashboard') {
    return destination
  }

  return resolveAccessibleLandingRoute(defaultDashboard, hasModule, hasPermission)
}
